// src/crm/campaigns/CampaignsPage.tsx
import { useState } from 'react'
import { useAppStore } from '../../shell/appStore'
import { useCampaigns, useCreateCampaign, useUpdateCampaign, type CampaignInput } from '../hooks/useCampaigns'
import { CampaignsList } from './CampaignsList'
import { CampaignForm } from './CampaignForm'
import type { Campaign } from '../../lib/types'
import { ApiClientError } from '../../lib/api'

type Mode = { type: 'list' } | { type: 'create' } | { type: 'edit'; campaign: Campaign }

export function CampaignsPage() {
  const appId = useAppStore((s) => s.activeAppId)
  const { data: campaigns, isLoading, isError } = useCampaigns(appId)
  const [mode, setMode] = useState<Mode>({ type: 'list' })
  const [formError, setFormError] = useState<string | null>(null)

  const createMutation = useCreateCampaign(appId ?? '')
  const updateMutation = useUpdateCampaign(
    mode.type === 'edit' ? mode.campaign.id : '',
    appId ?? '',
  )

  function handleSubmit(data: CampaignInput) {
    setFormError(null)
    if (mode.type === 'create') {
      createMutation.mutate(data, {
        onSuccess: () => setMode({ type: 'list' }),
        onError: (err) => {
          setFormError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      })
    } else if (mode.type === 'edit') {
      updateMutation.mutate(data, {
        onSuccess: () => setMode({ type: 'list' }),
        onError: (err) => {
          setFormError(err instanceof ApiClientError ? err.message : 'Erreur inattendue')
        },
      })
    }
  }

  if (!appId) {
    return (
      <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Sélectionnez une app pour voir ses campagnes.
      </div>
    )
  }

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '900px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--fg)' }}>
          Campagnes
        </h1>
        {mode.type === 'list' && (
          <button
            onClick={() => { setMode({ type: 'create' }); setFormError(null) }}
            style={btnPrimary}
          >
            + Nouvelle campagne
          </button>
        )}
      </div>

      {mode.type !== 'list' ? (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '10px',
          padding: '1.5rem',
          maxWidth: '540px',
        }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--fg)', marginBottom: '1.25rem' }}>
            {mode.type === 'create' ? 'Nouvelle campagne' : `Modifier « ${mode.campaign.name} »`}
          </h2>
          <CampaignForm
            initial={mode.type === 'edit' ? mode.campaign : undefined}
            onSubmit={handleSubmit}
            onCancel={() => { setMode({ type: 'list' }); setFormError(null) }}
            isPending={createMutation.isPending || updateMutation.isPending}
            error={formError}
          />
        </div>
      ) : (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '10px',
          overflow: 'hidden',
        }}>
          {isLoading ? (
            <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement…</div>
          ) : isError ? (
            <div style={{ padding: '2rem', color: 'var(--arancio)', fontSize: '0.875rem' }}>
              Erreur lors du chargement des campagnes.
            </div>
          ) : (
            <CampaignsList
              campaigns={campaigns ?? []}
              appId={appId}
              onEdit={(c) => { setMode({ type: 'edit', campaign: c }); setFormError(null) }}
            />
          )}
        </div>
      )}
    </div>
  )
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--arancio)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '0.4375rem 1rem',
  fontSize: '0.875rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
}
